import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Descriptions, Table, Tag, Spin, message, Empty } from 'antd';
import { benchmarkApi, useCaseExecutionApi, benchmarkMetricApi } from '../../services/api';
import { formatDateTime } from '../../utils/dateUtils';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import ResultStatsDisplay from '../../components/ResultStatsDisplay';

function BenchmarkDetail() {
  const { id } = useParams();
  const { currentScenario } = useWorkspace();
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [benchmarkInfo, setBenchmarkInfo] = useState(null);
  const [caseExecutions, setCaseExecutions] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  // 指标信息相关状态 - 必须在所有条件渲染之前声明
  const [metricsList, setMetricsList] = useState([]);
  const [metricsLoading, setMetricsLoading] = useState(false);

  // 要素抽取场景的统计数据
  const [aleMetricData, setAleMetricData] = useState(null);

  useEffect(() => {
    fetchBenchmarkDetail();
  }, [id]);

  useEffect(() => {
    if (benchmarkInfo?.taskExecutionHistoryId) {
      fetchCaseExecutions();
    }
  }, [benchmarkInfo, pagination.current, pagination.pageSize]);

  // 获取指标信息的副作用
  useEffect(() => {
    if (benchmarkInfo?.id) {
      fetchMetrics();
    }
  }, [benchmarkInfo]);

  const fetchBenchmarkDetail = async () => {
    setLoading(true);
    try {
      const response = await benchmarkApi.getDetail(id);
      setBenchmarkInfo(response);
    } catch (error) {
      console.error('获取Benchmark详情失败:', error);
      message.error('获取Benchmark详情失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchCaseExecutions = async () => {
    setDetailLoading(true);
    try {
      const params = {
        taskExecutionHistoryId: benchmarkInfo.taskExecutionHistoryId,
        pageNum: pagination.current,
        pageSize: pagination.pageSize,
      };
      
      const response = await useCaseExecutionApi.getList(params);
      setCaseExecutions(response.list || []);
      setPagination(prev => ({
        ...prev,
        total: response.total || 0,
      }));
    } catch (error) {
      console.error('获取用例执行明细失败:', error);
      message.error('获取用例执行明细失败');
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchMetrics = async () => {
    setMetricsLoading(true);
    try {
      const response = await benchmarkMetricApi.getList({ 
        benchmarkId: benchmarkInfo.id,
        pageSize: 100 
      });
      const list = response.list || [];
      setMetricsList(list);

      // 检查是否有 aleMetricValue 字段有值的指标
      const aleMetric = list.find(metric => metric.aleMetricValue);
      if (aleMetric && aleMetric.aleMetricValue) {
        try {
          const parsedData = typeof aleMetric.aleMetricValue === 'string'
            ? JSON.parse(aleMetric.aleMetricValue)
            : aleMetric.aleMetricValue;
          setAleMetricData(parsedData);
        } catch (parseError) {
          console.error('解析 aleMetricValue 失败:', parseError);
          setAleMetricData(null);
        }
      }
    } catch (error) {
      console.error('获取指标信息失败:', error);
      setMetricsList([]);
    } finally {
      setMetricsLoading(false);
    }
  };

  const handleTableChange = (paginationConfig) => {
    setPagination(paginationConfig);
  };

  const getStatusTag = (status) => {
    const statusMap = {
      'PENDING': { color: 'default', text: '待执行' },
      'RUNNING': { color: 'processing', text: '执行中' },
      'SUCCESS': { color: 'success', text: '执行成功' },
      'FAILED': { color: 'error', text: '执行失败' },
      'CANCELLED': { color: 'warning', text: '已取消' }
    };
    const config = statusMap[status] || statusMap['PENDING'];
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const getBenchmarkTypeColor = (type) => {
    const colorMap = {
      '基线benchmark': 'blue',
      '样本验收benchmark': 'green',
      '线上周期评测benchmark': 'orange'
    };
    return colorMap[type] || 'default';
  };

  const caseExecutionColumns = [
    {
      title: '执行ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '用例名称',
      dataIndex: 'caseName',
      key: 'caseName',
      width: 200,
    },
    {
      title: '执行状态',
      dataIndex: 'executionStatus',
      key: 'executionStatus',
      width: 100,
      render: getStatusTag
    },
    {
      title: '开始时间',
      dataIndex: 'startTime',
      key: 'startTime',
      width: 180,
      render: (time) => formatDateTime(time),
    },
    {
      title: '结束时间',
      dataIndex: 'endTime',
      key: 'endTime',
      width: 180,
      render: (time) => formatDateTime(time),
    },
    {
      title: '执行耗时',
      dataIndex: 'duration',
      key: 'duration',
      width: 100,
      render: (duration) => duration ? `${duration}s` : '-',
    },
    {
      title: '执行结果/错误信息',
      dataIndex: 'executionResultData',
      key: 'executionResult',
      width: 300,
      ellipsis: true,
      render: (executionResultData, record) => {
        if (record.executionStatus === 'SUCCESS') {
          return executionResultData || '-';
        } else if (record.executionStatus === 'FAILED') {
          return (
            <span className="text-red-600">
              {record.errorMessage || '-'}
            </span>
          );
        }
        return '-';
      },
    },
    {
      title: '断言结果',
      dataIndex: 'assertionResult',
      key: 'assertionResult',
      width: 200,
      ellipsis: true,
      render: (assertionResult) => {
        if (!assertionResult) return '-';
        
        try {
          const result = typeof assertionResult === 'string' 
            ? JSON.parse(assertionResult) 
            : assertionResult;
          
          return (
            <div>
              <div>
                {result.passed ? (
                  <span className="text-green-600">✓ {result.message || '断言通过'}</span>
                ) : (
                  <span className="text-red-600">✗ {result.message || '断言失败'}</span>
                )}
              </div>
              {result.details && (
                <div className="text-xs text-gray-500 mt-1">
                  {result.details.error || ''}
                </div>
              )}
            </div>
          );
        } catch (e) {
          return assertionResult;
        }
      },
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin size="large" />
      </div>
    );
  }

  if (!benchmarkInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center py-8">
          <p className="text-gray-500">未找到Benchmark信息</p>
        </div>
      </div>
    );
  }

  // 解析附件信息
  let attachmentList = [];
  if (benchmarkInfo.attachmentInfo) {
    try {
      const attachmentData = typeof benchmarkInfo.attachmentInfo === 'string' 
        ? JSON.parse(benchmarkInfo.attachmentInfo) 
        : benchmarkInfo.attachmentInfo;
      
      if (Array.isArray(attachmentData)) {
        attachmentList = attachmentData;
      }
    } catch (e) {
      console.error('解析附件信息失败:', e);
    }
  }

  return (
    <div className="min-h-screen bg-white p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Benchmark 详情</h1>
          <p className="text-gray-500 mt-1">查看 Benchmark 的详细信息和用例执行明细</p>
        </div>

        <Card title="基本信息" className="mb-4">
          <Descriptions column={2} bordered>
            <Descriptions.Item label="Benchmark ID">
              {benchmarkInfo.id}
            </Descriptions.Item>
            <Descriptions.Item label="Benchmark类型">
              <Tag color={getBenchmarkTypeColor(benchmarkInfo.benchmarkType)}>
                {benchmarkInfo.benchmarkType}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="版本号">
              <Tag color="geekblue">{benchmarkInfo.benchmarkVersion}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="业务场景">
              <Tag color="purple">{benchmarkInfo.businessScenarioName}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="用例数量">
              <span className="font-medium text-blue-600">
                {benchmarkInfo.useCaseCount}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {formatDateTime(benchmarkInfo.gmtCreate)}
            </Descriptions.Item>
            <Descriptions.Item label="创建人">
              {benchmarkInfo.creator}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {benchmarkInfo.remark && (
          <Card title="备注信息" className="mb-4">
            <div className="text-gray-700 whitespace-pre-wrap">
              {benchmarkInfo.remark}
            </div>
          </Card>
        )}

        {attachmentList.length > 0 && (
          <Card title="附件信息" className="mb-4">
            <div className="space-y-2">
              {attachmentList.map((attachment, index) => (
                <div 
                  key={index} 
                  className="flex items-center justify-between p-3 bg-gray-50 rounded hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-blue-500">📎</span>
                    <span className="text-gray-700">{attachment.attachmentName}</span>
                  </div>
                  <span className="text-gray-500 text-sm">ID: {attachment.attachmentId}</span>
                </div>
              ))}
            </div>
          </Card>
        )}

        <Card 
          title="指标详情" 
          className="mb-4"
          loading={metricsLoading}
        >
          {/* 要素抽取场景且有 aleMetricValue 数据时，展示统计组件 */}
          {currentScenario?.scenarioCategory === 'FEATURE_EXTRACTION' && aleMetricData ? (
            <ResultStatsDisplay data={aleMetricData} />
          ) : metricsList.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {metricsList.map((metric, index) => (
                <div 
                  key={index} 
                  className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-600">
                      {metric.metricType === 'judge' ? '评测指标' : '过程指标'}
                    </span>
                    <Tag color={metric.metricType === 'judge' ? 'blue' : 'green'}>
                      {metric.metricType}
                    </Tag>
                  </div>
                  <div className="text-lg font-semibold text-gray-800 mb-1">
                    {metric.metricName}
                  </div>
                  <div className="text-2xl font-bold text-blue-600">
                    {metric.metricValue !== null && metric.metricValue !== undefined 
                      ? metric.metricValue 
                      : '-'}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Empty 
              description="暂无指标详情" 
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        </Card>

        <Card title="用例执行明细">
          <div>
            <Table
              columns={caseExecutionColumns}
              dataSource={caseExecutions}
              rowKey="id"
              loading={detailLoading}
              pagination={{
                ...pagination,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `第 ${range[0]}-${range[1]} 条/总共 ${total} 条`,
              }}
              onChange={handleTableChange}
              scroll={{ x: 1400 }}
            />
          </div>
        </Card>
      </div>
    </div>
  );
}

export default BenchmarkDetail;
